// ClusterMarker.tsx - Steve Jobs Edition: Subtle, Clean, Purposeful
'use client';

import React from 'react';

export interface ClusterMarkerProps {
  pointCount: number;
  getPointCount: () => number;
  clusterId?: string | number;
  clusterType?: 'free' | 'powered' | 'mixed' | 'premium';
  size?: 'small' | 'medium' | 'large';
  isExpanded?: boolean;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const getSizeFromPointCount = (pointCount: number): 'small' | 'medium' | 'large' => {
  if (pointCount < 10) return 'small';
  if (pointCount < 50) return 'medium';
  return 'large';
};

// 🎨 Steve Jobs Principle: "Design is not just what it looks like. Design is how it works."
// Subtle color shifts that communicate meaning without screaming
const getClusterColors = (clusterType: ClusterMarkerProps['clusterType']) => {
  switch (clusterType) {
    case 'free':
      return {
        background: '#10b981',      // Emerald green - nature, free
        border: '#059669',           // Darker emerald
        text: '#ffffff',
        shadow: '0 2px 8px rgba(16, 185, 129, 0.25)',
        gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        ringColor: 'rgba(16, 185, 129, 0.2)' // Subtle outer ring
      };
    case 'powered':
      return {
        background: '#3b82f6',      // Blue - electricity, premium
        border: '#2563eb',
        text: '#ffffff',
        shadow: '0 2px 8px rgba(59, 130, 246, 0.25)',
        gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
        ringColor: 'rgba(59, 130, 246, 0.2)'
      };
    case 'premium':
      return {
        background: '#f59e0b',      // Amber - warmth, quality
        border: '#d97706',
        text: '#ffffff',
        shadow: '0 2px 8px rgba(245, 158, 11, 0.25)',
        gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        ringColor: 'rgba(245, 158, 11, 0.2)'
      };
    case 'mixed':
    default:
      return {
        background: '#6366f1',      // Indigo - versatile, modern
        border: '#4f46e5',
        text: '#ffffff',
        shadow: '0 2px 8px rgba(99, 102, 241, 0.25)',
        gradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
        ringColor: 'rgba(99, 102, 241, 0.2)'
      };
  }
};

const getSizeDimensions = (size: ClusterMarkerProps['size']) => {
  switch (size) {
    case 'small':
      return { width: 44, height: 44, fontSize: 13, borderWidth: 2.5 };
    case 'large':
      return { width: 64, height: 64, fontSize: 17, borderWidth: 3 };
    case 'medium':
    default:
      return { width: 54, height: 54, fontSize: 15, borderWidth: 2.5 };
  }
};

const ClusterMarker: React.FC<ClusterMarkerProps> = ({
  pointCount,
  getPointCount,
  clusterId,
  clusterType = 'mixed',
  size,
  isExpanded = false,
  onClick,
  onMouseEnter,
  onMouseLeave,
}) => {
  const markerSize = size || getSizeFromPointCount(pointCount);
  const colors = getClusterColors(clusterType);
  const dimensions = getSizeDimensions(markerSize);
  
  const displayCount = pointCount >= 1000 ? 
    `${Math.floor(pointCount / 1000)}k+` : 
    pointCount.toString();

  return (
    <div
      className="cluster-marker"
      style={{
        width: dimensions.width,
        height: dimensions.height,
        borderRadius: '50%',
        background: colors.gradient, // Subtle gradient instead of flat color
        border: `${dimensions.borderWidth}px solid ${colors.border}`,
        color: colors.text,
        fontSize: `${dimensions.fontSize}px`,
        fontWeight: '600', // Slightly lighter than bold - more refined
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: colors.shadow,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isExpanded ? 'scale(1.1)' : 'scale(1)',
        zIndex: isExpanded ? 1000 : 999,
        userSelect: 'none',
        // Subtle outer ring for depth
        outline: `4px solid ${colors.ringColor}`,
        outlineOffset: '2px',
        letterSpacing: '-0.02em', // Tighter, more refined typography
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick?.();
      }}
      onMouseEnter={(e) => {
        e.preventDefault();
        onMouseEnter?.();
      }}
      onMouseLeave={(e) => {
        e.preventDefault();
        onMouseLeave?.();
      }}
    >
      {displayCount}
    </div>
  );
};

export default ClusterMarker;

export const determineClusterType = (featureDescriptions: string[][] | string[] | any): ClusterMarkerProps['clusterType'] => {
  if (!featureDescriptions || featureDescriptions.length === 0) return 'mixed';
  
  let allDescriptions: string[] = [];
  
  try {
    const descriptions = Array.isArray(featureDescriptions) ? featureDescriptions : [featureDescriptions];
    allDescriptions = descriptions
      .flat(2)
      .filter(desc => desc && typeof desc === 'string')
      .map(desc => desc.toLowerCase());
  } catch (error) {
    console.warn('Error processing feature descriptions for cluster type:', error);
    return 'mixed';
  }
  
  if (allDescriptions.length === 0) return 'mixed';
  
  const freeCount = allDescriptions.filter(desc => 
    desc.includes('free') || 
    desc.includes('free camping') || 
    desc.includes('free overnight')
  ).length;
  
  const poweredCount = allDescriptions.filter(desc => 
    desc.includes('powered') || 
    desc.includes('electric') || 
    desc.includes('electrical') ||
    desc.includes('power')
  ).length;
  
  const premiumCount = allDescriptions.filter(desc => 
    desc.includes('cabin') || 
    desc.includes('glamping') || 
    desc.includes('resort') ||
    desc.includes('luxury') ||
    desc.includes('premium')
  ).length;
  
  const totalFeatures = allDescriptions.length;
  const freeRatio = freeCount / totalFeatures;
  const poweredRatio = poweredCount / totalFeatures;
  const premiumRatio = premiumCount / totalFeatures;
  
  if (premiumRatio > 0.3) return 'premium';
  if (freeRatio > 0.5 && poweredRatio < 0.2) return 'free';
  if (poweredRatio > 0.5 && freeRatio < 0.2) return 'powered';
  
  return 'mixed';
};

// Create DOM element for Google Maps Advanced Markers
export const createClusterMarkerElement = (
  pointCount: number,
  clusterType: ClusterMarkerProps['clusterType'],
  clusterId: string | number,
  onClick: () => void,
  onMouseEnter?: () => void,
  onMouseLeave?: () => void
): HTMLElement => {
  const container = document.createElement('div');
  container.className = 'cluster-marker-container';
  container.style.pointerEvents = 'auto';
  
  const size = getSizeFromPointCount(pointCount);
  const colors = getClusterColors(clusterType);
  const dimensions = getSizeDimensions(size);
  
  const displayCount = pointCount >= 1000 ? 
    `${Math.floor(pointCount / 1000)}k+` : 
    pointCount.toString();
  
  const marker = document.createElement('div');
  marker.className = 'cluster-marker';
  
  // Refined styling - Steve Jobs would approve
  marker.style.width = dimensions.width + 'px';
  marker.style.height = dimensions.height + 'px';
  marker.style.borderRadius = '50%';
  marker.style.background = colors.gradient; // Subtle gradient
  marker.style.border = dimensions.borderWidth + 'px solid ' + colors.border;
  marker.style.color = colors.text;
  marker.style.fontSize = dimensions.fontSize + 'px';
  marker.style.fontWeight = '600'; // Refined weight
  marker.style.display = 'flex';
  marker.style.alignItems = 'center';
  marker.style.justifyContent = 'center';
  marker.style.cursor = 'pointer';
  marker.style.boxShadow = colors.shadow;
  marker.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
  marker.style.userSelect = 'none';
  marker.style.fontFamily = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif';
  marker.style.pointerEvents = 'none';
  marker.style.letterSpacing = '-0.02em'; // Tighter typography
  
  // Subtle outer ring for depth
  marker.style.outline = '4px solid ' + colors.ringColor;
  marker.style.outlineOffset = '2px';
  
  marker.textContent = displayCount;
    
  container.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    onClick();
  });
  
  marker.addEventListener('mouseenter', (e) => {
    e.preventDefault();
    marker.style.transform = 'scale(1.08)'; // Slightly more pronounced
    marker.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.3)'; // Lift on hover
    onMouseEnter?.();
  });
  
  marker.addEventListener('mouseleave', (e) => {
    e.preventDefault();
    marker.style.transform = 'scale(1)';
    marker.style.boxShadow = colors.shadow;
    onMouseLeave?.();
  });
  
  marker.addEventListener('mousedown', () => {
    marker.style.transform = 'scale(0.96)'; // Subtle press
  });
  
  marker.addEventListener('mouseup', () => {
    marker.style.transform = 'scale(1.08)';
  });
  
  container.setAttribute('aria-label', `Cluster of ${pointCount} campsites`);
  container.appendChild(marker);
  
  return container;
};
